import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Title } from 'src/components/AuthNew/components/Title';
import { Description } from 'src/components/AuthNew/components/Description';
import { Form, Formik } from 'formik';
import { ButtonWithLoader } from 'src/components/AuthNew/components/ButtonWithLoader/ButtonWithLoader';
import { resetPhoneFormat, setPhoneFormat } from 'src/components/AuthNew/utils';
import { getClientUnconfirmedPhone } from 'src/store/client/selectors';
import type {
  SetFormType,
  SetRequestStatus,
  SetSharedState,
  SharedState,
} from 'src/components/AuthNew/types';
import { AuthFormType, RequestStatus } from 'src/components/AuthNew/types';
import type { UseGetNewCodeReturn } from 'src/components/AuthNew/hooks';
import type { FormikHelpers, FormikProps } from 'formik/dist/types';
import { mapSubmitErrorsToFields } from 'src/components/AuthNew/errors';
import { i18n, useTranslation } from '@lt/localization';
import styles from './confirmPhoneForm.module.css';
import type { FormValues } from './types';
import { FormFields } from './types';
import { Content } from '../Content';
import { PhoneInput } from '../LoginForm/components';
import { validationSchema } from './validationSchema';

type Props = {
  sharedState: SharedState | undefined;
  timeLeftToDeliver: number;
  setRequestStatus: SetRequestStatus;
  setSharedState: SetSharedState;
  setFormType: SetFormType;
  handleGetNewCode: UseGetNewCodeReturn['handleGetNewCode'];
};

const confirmPhoneFormErrorMap = {
  otp_phone_taken: {
    name: FormFields.Phone,
    message: i18n.t('checkout.registrationForm.phone.error'),
  },
  otp_sms_already_sent: {
    name: FormFields.Phone,
    // в качестве message передаем пробел, чтобы выделить инпут красным
    message: ' ',
  },
};

export const ConfirmPhoneForm = ({
  setRequestStatus,
  sharedState,
  timeLeftToDeliver,
  setSharedState,
  setFormType,
  handleGetNewCode,
}: Props) => {
  const [hasError, setHasError] = useState(false);

  // ref для доступа к контексту формика вне компонента <Formik></Formik>
  const formikRef = useRef<FormikProps<FormValues> | null>(null);
  const { t } = useTranslation();
  const unconfirmedPhone = useSelector(getClientUnconfirmedPhone);

  useEffect(() => {
    if (timeLeftToDeliver === 0) {
      setHasError(false);
      if (formikRef.current) {
        // сбрасываем ошибку с инпута
        formikRef.current.setFieldError(FormFields.Phone, '');
        formikRef.current.setFieldTouched(FormFields.Phone);
      }
    }
  }, [timeLeftToDeliver]);

  const handleSubmit = async (
    { phone }: FormValues,
    formikHelpers: FormikHelpers<FormValues>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> => {
    try {
      const newPhone = resetPhoneFormat(phone);
      setRequestStatus(RequestStatus.Submitting);
      setSharedState((prev) => ({
        ...prev,
        unconfirmed_phone: newPhone,
      }));
      const response = await handleGetNewCode({
        phone: newPhone,
        client_id: sharedState?.id || undefined,
        event: 'sign_up',
        captchaCallbackSuccess: () => {
          setFormType(AuthFormType.ConfirmPhoneWithSMSCode);
        },
      });
      if (!response.success) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { errors } = (response as any).error?.response?.data;
        mapSubmitErrorsToFields(confirmPhoneFormErrorMap)(
          formikHelpers.setFieldError,
        )(errors);
        setHasError(true);
        setRequestStatus(RequestStatus.Failed);
        return;
      }
      setFormType(AuthFormType.ConfirmPhoneWithSMSCode);
      setRequestStatus(RequestStatus.Idle);
    } catch (error) {
      setRequestStatus(RequestStatus.Failed);
    }
  };

  return (
    <Formik<FormValues>
      initialValues={{
        phone: setPhoneFormat(
          sharedState?.phone_mask ?? '',
          sharedState?.unconfirmed_phone || unconfirmedPhone || '',
        ),
      }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      innerRef={formikRef}
    >
      {({ isSubmitting, isValid }) => (
        <Form>
          <div>
            <Title>{t('checkout.confirmPhone.title')}</Title>
            <Description>{t('checkout.confirmPhone.description')}</Description>
            <Content>
              <PhoneInput
                setSharedState={setSharedState}
                sharedState={sharedState}
                name={FormFields.Phone}
              />
              {hasError && (
                <div className={styles.timeError}>
                  {t('checkout.confirmPhone.requestNewSms', {
                    seconds: timeLeftToDeliver,
                  })}
                </div>
              )}
              <ButtonWithLoader
                size="large"
                primary
                type="submit"
                isLoading={isSubmitting}
                disabled={isSubmitting || !isValid}
                width="100%"
              >
                {t('checkout.loginForm.continue')}
              </ButtonWithLoader>
            </Content>
          </div>
        </Form>
      )}
    </Formik>
  );
};
